import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { PROVENANCE_SOURCE_MANUAL } from './provenanceSources';

/**
 * U6 S3 (C5-ff(i)) — route a manual product-cost edit through `write_junction_cost`,
 * the ONE junction-cost-write boundary (C5, 20260624000005). The product_distributors
 * junction is cost-canonical; the AFTER trigger `sync_products_primary_distributor`
 * read-copies `cost_usd` + `provenance.cost_usd` → products, so the denormalized
 * `products.cost_usd` the UI reads stays coherent (no stale display).
 *
 * This REPLACES the non-durable direct `products.cost_usd` write (the KA3-F2 hazard —
 * a later junction write's sync trigger silently reverted a products-only edit). By
 * writing the junction, the manual cost is the canonical value the trigger propagates.
 * NEVER writes `products` directly.
 *
 * The entered cost enters as the SHEET cost (`p_cost_api` NULL) → the RPC resolves
 * `cost_usd = COALESCE(cost_api, cost_sheet)` = the entered value; it also writes the
 * full cost-provenance envelope (source=manual, reason, set_at/set_by/previous_value).
 *
 * Extracted as a seam so the routing is unit-testable without a full ProductDetail
 * render (the RPC → sync-trigger → products behavior is DB-proven by c5JunctionCost).
 */
export async function writeManualCost(
  client: SupabaseClient<Database>,
  args: { productId: string; distributorId: string; cost: number; reason: string | null }
): Promise<{ error: { message: string } | null }> {
  const { error } = await client.rpc('write_junction_cost', {
    p_product_id: args.productId,
    p_distributor_id: args.distributorId,
    p_cost_sheet: args.cost,
    p_cost_api: null,
    p_cost_sheet_base: null,
    p_source: PROVENANCE_SOURCE_MANUAL,
    p_reason: args.reason,
    // The generated Args type is non-null-strict (p_cost_api/p_cost_sheet_base/p_reason
    // typed number/string); the DB params are nullable — `as never` matches the
    // OverrideEditModal update_price_override precedent for a nullable-arg RPC call.
  } as never);
  return { error: error ? { message: error.message } : null };
}
